import type React from "react"
import { useEffect, useRef, useState } from "react"
import {
  PlayerWrapper,
  TrackInfo,
  ControlsRow,
  MainControls,
  SecondaryControls,
  ProgressContainer,
  IconButton,
  Time,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarHandle,
  ProgressInput,
  DownloadButton,
  SpeedSelector,
  SpeedControl,
  OptimizationBanner,
  TooltipWrapper,
  TooltipText,
  MobileTooltipText,
  MobileTooltipOverlay,
  MobileTooltipCloseButton,
  DownloadTooltipWrapper,
  DownloadTooltipText,
  colors
} from "./styles"
import PlayIcon from "../../assets/player/custom-player-play.svg"
import PauseIcon from "../../assets/player/custom-player-pause.svg"
import downloadFileIcon from "../../assets/icons/download-file.svg"
import InfoIcon from "../../assets/icons/info.svg"
import Icon from "../Icon"
import spinner from "../../assets/spinnerloader.svg"
import { useWindowSize } from "../../hooks/useWindowSize"

type Props = {
  audioUrl: string
  title?: string
  artist?: string
  isCompressed?: boolean
  blockDownload?: boolean
  isOwner?: boolean
  canBeCompressed?: boolean
  optimizationRate?: number
}

const CustomAudioPlayer: React.FC<Props> = ({
  audioUrl,
  title,
  artist,
  blockDownload = true,
  isOwner = false,
  canBeCompressed,
  optimizationRate,
  isCompressed = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressRef = useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showMobileTooltip, setShowMobileTooltip] = useState(false)
  const { isMobile } = useWindowSize()
  const isSeeking = useRef(false)

  const toggleTooltip = () => {
    if (isMobile) setShowMobileTooltip(true)
  }

  const closeMobileTooltip = () => setShowMobileTooltip(false)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      if (!isSeeking.current) setCurrentTime(audio.currentTime)
    }

    const handleLoaded = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)

    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("loadedmetadata", handleLoaded)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("loadedmetadata", handleLoaded)
      audio.removeEventListener("ended", handleEnded)
    }
  }, [])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const resetSeek = () => {
    if (seekTimeoutRef.current) clearTimeout(seekTimeoutRef.current)
    seekTimeoutRef.current = setTimeout(() => {
      isSeeking.current = false
    }, 300)
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const value = parseFloat(e.target.value)
    isSeeking.current = true
    audio.currentTime = value
    setCurrentTime(value)
    resetSeek()
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const progressBar = progressRef.current
    if (!progressBar || !audioRef.current) return

    const rect = progressBar.getBoundingClientRect()
    const clickPosition = (e.clientX - rect.left) / rect.width
    const newTime = clickPosition * duration

    isSeeking.current = true
    audioRef.current.currentTime = newTime
    setCurrentTime(newTime)
    resetSeek()
  }

  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const rate = parseFloat(e.target.value)
    setPlaybackRate(rate)
    if (audioRef.current) audioRef.current.playbackRate = rate
  }

  const formatTime = (secs: number) => {
    const minutes = Math.floor(secs / 60)
    const seconds = Math.floor(secs % 60).toString().padStart(2, "0")
    return `${minutes}:${seconds}`
  }

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0

  const tooltipMessage = !isCompressed
    ? "Estamos comprimiendo este audio para reducir su peso sin perder calidad. Mientras tanto se pueden consumir el audio en su tamaño y formato original. Solo tú ves este mensaje."
    : "Klouser optimizó este audio para reducir su peso sin perder calidad. Esto mejora muchísimo la experiencia de tus suscriptores. Solo tú ves este mensaje."

  return (
    <PlayerWrapper>
      {title && (
        <TrackInfo>
          {title} {artist && <span style={{ fontWeight: 400, color: colors.textSecondary }}>– {artist}</span>}
        </TrackInfo>
      )}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <ControlsRow>
        <MainControls>
          <IconButton onClick={togglePlay}>
            <Icon icon={isPlaying ? PauseIcon : PlayIcon} color="white" />
          </IconButton>
          <ProgressContainer>
            <Time>{formatTime(currentTime)}</Time>
            <ProgressBarContainer ref={progressRef} onClick={handleProgressClick}>
              <ProgressBarFill progress={progressPercentage} />
              <ProgressBarHandle progress={progressPercentage} />
              <ProgressInput
                type="range"
                min={0}
                max={duration || 0}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
              />
            </ProgressBarContainer>
            <Time>{formatTime(duration)}</Time>
          </ProgressContainer>
        </MainControls>

        <SecondaryControls>
          <SpeedControl>
            <SpeedSelector value={playbackRate} onChange={handleSpeedChange}>
              {[0.75, 1, 1.25, 1.5, 2].map((v) => (
                <option key={v} value={v}>{v}x</option>
              ))}
            </SpeedSelector>
          </SpeedControl>

          {(!blockDownload || isOwner) && (
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <DownloadButton
                href={audioUrl}
                target="_blank"
                download={audioUrl.split("/").pop()?.split("?")[0]}
              >
                <Icon icon={downloadFileIcon} size="18px" color={colors.text} />
                {!isMobile && "Descargar"}
              </DownloadButton>
              {isOwner && (
                <DownloadTooltipWrapper>
                  <Icon icon={InfoIcon} size="16px" color="white" />
                  <DownloadTooltipText>
                    {blockDownload
                      ? "Solo tú puedes ver el botón de descarga"
                      : "Todos pueden ver el botón de descarga"}
                  </DownloadTooltipText>
                </DownloadTooltipWrapper>
              )}
            </div>
          )}
        </SecondaryControls>
      </ControlsRow>

      {isOwner && !!canBeCompressed && (
        <>
          <OptimizationBanner>
            {!isCompressed ? (
              <>
                💡 Klouser está optimizando este audio <Icon icon={spinner} size="12px" color="white" />
              </>
            ) : (
              `🚀 El peso del audio fue optimizado${optimizationRate ? ` un ${optimizationRate}%` : ""}`
            )}
            <TooltipWrapper>
              <div onClick={toggleTooltip} style={{ cursor: "pointer" }}>
                <Icon icon={InfoIcon} size="16px" color="white" />
              </div>
              {!isMobile && <TooltipText>{tooltipMessage}</TooltipText>}
            </TooltipWrapper>
          </OptimizationBanner>

          {showMobileTooltip && isMobile && (
            <MobileTooltipOverlay onClick={closeMobileTooltip}>
              <MobileTooltipText>
                <MobileTooltipCloseButton onClick={closeMobileTooltip}>✕</MobileTooltipCloseButton>
                {tooltipMessage}
              </MobileTooltipText>
            </MobileTooltipOverlay>
          )}
        </>
      )}
    </PlayerWrapper>
  )
}

export default CustomAudioPlayer
